import {
  PlusOutlined,
  EditOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageEnum, TABLEPAI } from "@/enums/pageEnum";
import { Col, Row, Form, Button, Modal } from "antd";
import { GetLiftStatusCols, LiftTcPfxBlkNumber } from "@/utils/commonConfig";
import NProgress from "nprogress";
import { PageTitle, CustomModal, BasicTable } from "@/components";
import {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useTransition,
} from "react";
import {
  ProFormText,
  ProForm,
  PageContainer,
  ProFormTextArea,
} from "@ant-design/pro-components";
import { creatOmsSchedule, getDetailInfo } from "@api";
import { useOmsDatePicker } from "@/hooks";
import useMessageRedux from "@/store/message-redux";

export default function AddNew() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigation = useNavigate();
  const { message } = useMessageRedux();
  const [seclectedLift, setSeclectedLift] = useState<any>();
  const [temporary, setTemporary] = useState<any>();
  const [isPending, startTransition] = useTransition();
  const { getOfftime, getOntime, offTime, onTime } = useOmsDatePicker();

  useEffect(() => {
    NProgress.done();
  }, []);

  const liftStatusCols = useMemo(() => {
    return GetLiftStatusCols(t, {
      title: t("lift"),
      width: 150,
      dataIndex: ["lift", "name"],
    });
  }, [t]);

  const getFormText = useCallback(
    (name: string, label?: string) => {
      const labText = t(label || name);
      return (
        <ProFormText name={name} disabled label={labText} placeholder={""} />
      );
    },
    [t]
  );

  const header = useMemo(() => {
    return {
      title: null,
      extra: [],
      breadcrumb: {
        items: [
          { title: <Link to={PageEnum.OMS_SCHEDULE}>{t("omsSched")}</Link> },
          { title: t("addNew") },
        ],
      },
    };
  }, [t]);

  return (
    <div className="bg-[#f2f2f2] dark:bg-[#000] typeDetailCalss">
      <PageTitle title={t("omsSched")} />
      <PageContainer header={header}>
        <ProForm
          layout={"horizontal"}
          form={form}
          className="bg-[#fff] dark:bg-[#141414] formDisabledColor py-5 min-h-[calc(100vh-165px)]"
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 12 }}
          submitter={false}
          onFinish={(values) => {
            Modal.confirm({
              title: "Save",
              icon: <ExclamationCircleFilled />,
              content: "Do you want save this item?",
              className: "loginModalName",
              onOk() {
                const offAt = offTime?.format();
                const onAt = onTime?.format();
                const { report, liftId } = values;
                startTransition(async () => {
                  try {
                    await creatOmsSchedule({ report, liftId, offAt, onAt });
                    navigation(PageEnum.OMS_SCHEDULE, {
                      state: { isReload: true },
                    });
                  } catch (error: any) {
                    message.error(error?.detail || error);
                  }
                });
              },
            });
          }}
        >
          <div className="text-[20px] font-medium pb-8 pl-5">
            {t("newSched")}
          </div>
          <ProForm.Item
            name="liftId"
            label={t("lift")}
            rules={[{ required: true, message: "Please select lift." }]}
          >
            {seclectedLift?.name}
            <CustomModal
              title={seclectedLift ? "Change" : "Select"}
              modalName="Select lift"
              isProForm={false}
              width={990}
              buttonCogfig={{
                icon: seclectedLift ? <EditOutlined /> : <PlusOutlined />,
                variant: "text",
              }}
              className="feedbackHistoryModal"
              isShowOkButton={true}
              onColseModal={() => {
                navigation(`${PageEnum.OMS_SCHEDULE}/add-new`, {
                  replace: true,
                });
              }}
              onClick={(_, callBack) => {
                if (!temporary) {
                  callBack?.(true);
                  return;
                }
                startTransition(async () => {
                  try {
                    const { data } = await getDetailInfo(
                      `lift/${temporary?.id}`
                    );
                    setSeclectedLift(temporary);
                    form?.setFieldsValue({
                      liftId: temporary?.id,
                      liftCompany: data?.body?.attribute?.maintenanceCompany,
                      telephone: data?.body?.building?.attribute?.telephone,
                      address: data?.body?.building?.attribute?.address,
                    });
                    callBack?.(false);
                  } catch (error: any) {
                    callBack?.(true);
                    message.error(error?.detail || error);
                  }
                });
              }}
            >
              <BasicTable
                columns={liftStatusCols}
                filterTableFields={[
                  {
                    ...LiftTcPfxBlkNumber(t),
                    fieldProps: { divClass: "w-[400px]" },
                  },
                  {
                    field: "postalCode",
                    label: t("postalCode"),
                    component: "text",
                    fieldProps: { style: { width: "115px" } },
                  },
                ]}
                isPagination={false}
                tableHeight={360}
                rowKey={(record: any) => record?.lift?.id}
                isDefaultToolBar={false}
                dataKey="data.body"
                className="min-h-max !px-0"
                requestUrl={TABLEPAI.CASE_LIFT_STATUS}
                longPolling
                isRefreshSelectedRows={false}
                onChangeSelected={(rowKeys, selectedRows) => {
                  if (!rowKeys.length) return;
                  setTemporary({ ...selectedRows[0]?.lift });
                }}
                rowSelection={{
                  type: "radio",
                  getCheckboxProps: (record: any) => ({
                    disabled: !!record.omsScheduleCnt,
                  }),
                }}
              />
            </CustomModal>
          </ProForm.Item>
          {getFormText("liftCompany", "LIFT_COMPANY")}
          {getFormText("telephone")}
          {getFormText("address")}
          {getOfftime}
          {getOntime}
          <ProFormTextArea
            name="report"
            rules={[
              { required: true, message: "Please input report description." },
            ]}
            label={t("reportDescr")}
            fieldProps={{ maxLength: 2000, showCount: true }}
          />
          <Row>
            <Col span={12} offset={7} className="text-right">
              <Button variant="outlined" onClick={() => navigation(-1)}>
                {t("cancel")}
              </Button>
              <Button
                type="primary"
                className="pr-4 pl-4 ml-3"
                loading={isPending}
                onClick={() => form?.submit?.()}
              >
                {t("ok")}
              </Button>
            </Col>
          </Row>
        </ProForm>
      </PageContainer>
    </div>
  );
}
